import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import DownloadIcon from '@mui/icons-material/Download';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RefreshIcon from '@mui/icons-material/Refresh';
import ReplayIcon from '@mui/icons-material/Replay';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  LinearProgress,
  Link as MuiLink,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import type { ChipProps } from '@mui/material';
import { useCallback, useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';

import { useFeatureFlagsContext } from '../../contexts/featureFlagContext';
import {
  CanvassingSessionPipelineJobStatus,
  CanvassingSessionPipelineStage,
  SessionTelemetryPlatform,
} from '../../graphql/__generated__/graphql';
import { resolveClientEnv } from '../../graphql/config/resolveClientEnv';
import { BrokerageCanvasserDirectoryFailureNotice } from '../BrokerageCanvasserDirectoryFailureNotice';
import { useBrokerageCanvasserDirectory, type BrokerageCanvasserDirectoryStatus } from '../BrokerageCanvasserDirectoryContext';
import { EmptyState } from '../EmptyState';
import { ErrorState } from '../ErrorState';
import { LoadingState } from '../LoadingState';
import { PageControlsBar } from '../PageControlsBar';
import { PageHeader } from '../PageHeader';
import type { CanvassingSessionFetchImpl } from './canvassingSessionApi';
import { retryDeadPipelineJob } from './retryDeadPipelineJobApi';
import { CanvassingSessionDetailPagination } from './CanvassingSessionDetailPagination';
import { CanvassingSessionGpsTraceMap } from './CanvassingSessionGpsTraceMap';
import { CanvassingSessionMapOverlayStatus } from './CanvassingSessionMapOverlayStatus';
import { MapOverlayRecordList, overlayRecordsInDisplayOrder } from './CanvassingSessionMapOverlayRecords';
import { MAP_OVERLAY_COPY } from './canvassingSessionMapOverlay';
import {
  formatCanvassingSessionDuration,
  formatCanvassingSessionMilliseconds,
  formatReceivedAndExpected,
} from './canvassingSessionFormatters';
import {
  getCanvassingSessionActivityKindLabel,
  getCanvassingSessionCaptureStatusLabel,
  getCanvassingSessionDoorVisitStatusLabel,
  getCanvassingSessionPipelineJobStatusLabel,
  getCanvassingSessionPipelineStageLabel,
  getCanvassingSessionProcessingStatusLabel,
} from './canvassingSessionLabels';
import {
  isCanvassingSessionId,
  useCanvassingSessionDetail,
  type CanvassingSessionDetail,
  type CanvassingSessionDetailScheduler,
} from './useCanvassingSessionDetail';
import {
  useCanvassingSessionGpsTrace,
  type CanvassingSessionGpsTracePublication,
  type CanvassingSessionGpsTraceState,
} from './useCanvassingSessionGpsTrace';
import {
  useCanvassingSessionMapOverlay,
  type UseCanvassingSessionMapOverlayResult,
} from './useCanvassingSessionMapOverlay';
import { CanvassingSessionTelemetrySection } from './CanvassingSessionTelemetrySection';
import {
  CanvassingSessionTranscriptionAction,
  CanvassingSessionTranscriptionSection,
} from './CanvassingSessionTranscription';
import { useCanvassingSessionTranscription } from './useCanvassingSessionTranscription';
import { CanvassingSessionTimestamp as LocalTimestamp } from './CanvassingSessionTimestamp';
import { useDeleteCanvassingSessionPipelineJob } from './useDeleteCanvassingSessionPipelineJob';
import {
  SESSION_RECORDING_COMPLETENESS_UNKNOWN_WARNING,
  SESSION_RECORDING_DOWNLOAD_PREPARING_MESSAGE,
  SESSION_RECORDING_DOWNLOAD_REQUESTED_MESSAGE,
  SESSION_RECORDING_INCOMPLETE_WARNING,
  SESSION_RECORDING_NOT_MERGED_MESSAGE,
  SESSION_RECORDING_PLAYBACK_ERROR_MESSAGE,
  SESSION_RECORDING_PLAYBACK_PREPARING_MESSAGE,
  useCanvassingSessionRecording,
  type UseCanvassingSessionRecordingResult,
} from './useCanvassingSessionRecording';

export type CanvassingSessionDetailPageProps = {
  fetchImpl?: CanvassingSessionFetchImpl;
  scheduler?: CanvassingSessionDetailScheduler;
};

type SectionCardProps = {
  title: string;
  helper?: string;
  children: ReactNode;
};

type MetricProps = {
  label: string;
  value: ReactNode;
};

const SAFE_RETURN_TO_PATTERN = /^\/canvassing-sessions(?:[?#]|$)/;

const isPositiveSafeInteger = (value: unknown): value is number => typeof value === 'number' && Number.isSafeInteger(value) && value > 0;

const SESSION_TELEMETRY_METADATA_NOTE = 'Reported by the latest available session telemetry.';

const formatReportedAppVersion = (value: string | null | undefined): string => {
  if (typeof value !== 'string' || value.trim() === '') return 'Not available';
  return value.trim();
};

const formatReportedOsVersion = (
  platform: SessionTelemetryPlatform | null | undefined,
  osVersion: string | null | undefined,
): string => {
  const version = typeof osVersion === 'string' ? osVersion.trim() : '';
  if (platform === SessionTelemetryPlatform.Android) return version ? `Android ${version}` : 'Android';
  if (platform === SessionTelemetryPlatform.Ios) return version ? `iOS ${version}` : 'iOS';
  return version || 'Not available';
};

const formatNullableBoolean = (value: boolean | null | undefined): string => {
  if (value === null || value === undefined) return '—';
  return value ? 'Yes' : 'No';
};

const sanitizeErrorSummary = (value: string | null | undefined): string => {
  if (typeof value !== 'string') return '—';
  const withoutControlCharacters = Array.from(value, character => {
    const code = character.charCodeAt(0);
    return code <= 31 || code === 127 ? ' ' : character;
  }).join('');
  const sanitized = withoutControlCharacters.replace(/\s+/g, ' ').trim();
  return sanitized || '—';
};

export const isSafeCanvassingSessionReturnTo = (returnTo: unknown): returnTo is string =>
  typeof returnTo === 'string' && !returnTo.startsWith('//') && SAFE_RETURN_TO_PATTERN.test(returnTo);

export const resolveCanvassingSessionReturnTo = (state: unknown): string => {
  const returnTo = typeof state === 'object' && state !== null ? Reflect.get(state, 'returnTo') : undefined;
  return isSafeCanvassingSessionReturnTo(returnTo) ? returnTo : '/canvassing-sessions';
};

const SectionCard = ({ title, helper, children }: SectionCardProps) => (
  <Paper variant="outlined" sx={{ p: 3 }}>
    <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 700, mb: helper ? 0.5 : 2 }}>
      {title}
    </Typography>
    {helper && (
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {helper}
      </Typography>
    )}
    {children}
  </Paper>
);

const Metric = ({ label, value }: MetricProps) => (
  <Box>
    <Typography variant="caption" color="text.secondary" component="dt">
      {label}
    </Typography>
    <Typography variant="body1" component="dd" sx={{ m: 0, fontWeight: 600 }}>
      {value}
    </Typography>
  </Box>
);

const PipelineJobStatusChip = ({ status }: { status: Parameters<typeof getCanvassingSessionPipelineJobStatusLabel>[0] }) => {
  const color: ChipProps['color'] = status === 'DEAD' ? 'error' : status === 'DONE' ? 'success' : status === 'LEASED' ? 'warning' : 'default';
  return <Chip label={getCanvassingSessionPipelineJobStatusLabel(status)} size="small" color={color} variant="outlined" />;
};

const DoorVisitLink = ({ doorVisitId }: { doorVisitId: unknown }) => {
  if (!isPositiveSafeInteger(doorVisitId)) return <>—</>;

  return (
    <MuiLink component={Link} to={`/door-visits/${doorVisitId}`}>
      {doorVisitId}
    </MuiLink>
  );
};

const PipelineDoorVisitLink = ({
  stage,
  doorVisitId,
}: {
  stage: CanvassingSessionPipelineStage;
  doorVisitId: unknown;
}) => {
  if (stage !== CanvassingSessionPipelineStage.Submit) return <>—</>;

  return <DoorVisitLink doorVisitId={doorVisitId} />;
};

type CanvassingSessionPageHeaderProps = {
  returnTo: string;
  sessionId?: string;
};

const CanvassingSessionPageHeader = ({ returnTo, sessionId }: CanvassingSessionPageHeaderProps) => (
  <PageHeader
    title="Canvassing Session"
    subtitle={sessionId === undefined ? undefined : `Session ${sessionId}`}
    backLink={{ label: 'Canvassing Sessions', to: returnTo }}
  />
);

const UnavailableState = ({ returnTo, sessionId }: { returnTo: string; sessionId?: string }) => (
  <Box>
    <CanvassingSessionPageHeader returnTo={returnTo} sessionId={sessionId} />
    <EmptyState title="This canvassing session is unavailable." />
  </Box>
);

const SessionControlsBar = ({
  refreshing,
  onRefresh,
  recordingControls,
  transcriptionControl,
}: {
  refreshing: boolean;
  onRefresh: () => void;
  recordingControls?: ReactNode;
  transcriptionControl?: ReactNode;
}) => (
  <PageControlsBar label="Session diagnostics">
    <Button
      variant="outlined"
      startIcon={refreshing ? <CircularProgress size={16} aria-hidden /> : <RefreshIcon />}
      onClick={onRefresh}
      disabled={refreshing}
      aria-busy={refreshing}
    >
      Refresh
    </Button>
    {recordingControls}
    {transcriptionControl}
  </PageControlsBar>
);

const SessionRecordingControls = ({
  unavailable,
  descriptionId,
  recording,
}: {
  unavailable: boolean;
  descriptionId: string;
  recording: UseCanvassingSessionRecordingResult;
}) => {
  const playbackPreparing = recording.playback.status === 'loading';
  const downloadPreparing = recording.download.status === 'preparing';

  return (
    <>
      <Button
        variant="outlined"
        startIcon={playbackPreparing ? <CircularProgress size={16} aria-hidden /> : <PlayArrowIcon />}
        onClick={recording.requestPlayback}
        disabled={unavailable || playbackPreparing}
        aria-busy={playbackPreparing}
        aria-describedby={unavailable ? descriptionId : undefined}
      >
        Play session recording
      </Button>
      <Button
        variant="outlined"
        startIcon={downloadPreparing ? <CircularProgress size={16} aria-hidden /> : <DownloadIcon />}
        onClick={recording.requestDownload}
        disabled={unavailable || downloadPreparing}
        aria-busy={downloadPreparing}
        aria-describedby={unavailable ? descriptionId : undefined}
      >
        Download recording
      </Button>
    </>
  );
};

const SessionRecordingRegion = ({
  unavailable,
  descriptionId,
  recording,
}: {
  unavailable: boolean;
  descriptionId: string;
  recording: UseCanvassingSessionRecordingResult;
}) => {
  const playback = recording.playback;
  const download = recording.download;
  const showRegion =
    unavailable ||
    recording.warnings.incomplete ||
    recording.warnings.completenessUnknown ||
    playback.status !== 'idle' ||
    download.status !== 'idle';

  if (!showRegion) return null;

  return (
    <Paper variant="outlined" component="section" aria-label="Session recording" sx={{ p: 2, mb: 3 }}>
      <Stack spacing={1.5}>
        {unavailable && (
          <Typography id={descriptionId} variant="body2" color="text.secondary">
            {SESSION_RECORDING_NOT_MERGED_MESSAGE}
          </Typography>
        )}
        {recording.warnings.incomplete && (
          <Typography variant="body2">{SESSION_RECORDING_INCOMPLETE_WARNING}</Typography>
        )}
        {recording.warnings.completenessUnknown && (
          <Typography variant="body2">{SESSION_RECORDING_COMPLETENESS_UNKNOWN_WARNING}</Typography>
        )}
        {playback.status === 'loading' && (
          <Typography role="status" aria-live="polite" variant="body2" color="text.secondary">
            {SESSION_RECORDING_PLAYBACK_PREPARING_MESSAGE}
          </Typography>
        )}
        {playback.status === 'error' && <Alert severity="error">{playback.message}</Alert>}
        {playback.status === 'media-error' && (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={recording.retryPlayback}>
                Retry recording
              </Button>
            }
          >
            {SESSION_RECORDING_PLAYBACK_ERROR_MESSAGE}
          </Alert>
        )}
        {playback.status === 'ready' && (
          <audio
            key={playback.actionId}
            ref={recording.bindAudio}
            controls
            preload="metadata"
            src={playback.url}
            aria-label="Canvassing session recording"
            onError={recording.reportMediaError}
            onTimeUpdate={recording.capturePosition}
            onPause={recording.capturePosition}
            style={{ display: 'block', width: '100%', maxWidth: '100%' }}
          />
        )}
        {download.status === 'preparing' && (
          <Typography role="status" aria-live="polite" variant="body2" color="text.secondary">
            {SESSION_RECORDING_DOWNLOAD_PREPARING_MESSAGE}
          </Typography>
        )}
        {download.status === 'requested' && (
          <Typography role="status" aria-live="polite" variant="body2" color="text.secondary">
            {SESSION_RECORDING_DOWNLOAD_REQUESTED_MESSAGE}
          </Typography>
        )}
        {download.status === 'error' && <Alert severity="error">{download.message}</Alert>}
      </Stack>
    </Paper>
  );
};

const SessionOverview = ({
  session,
  isStructurelyAdmin,
  manualRefreshing,
  canvasserDirectoryStatus,
  resolveCanvasserLabel,
}: {
  session: CanvassingSessionDetail;
  isStructurelyAdmin: boolean;
  manualRefreshing: boolean;
  canvasserDirectoryStatus: BrokerageCanvasserDirectoryStatus;
  resolveCanvasserLabel: (canvasserId: string) => string;
}) => (
  <>
    {manualRefreshing && (
      <Stack role="status" aria-live="polite" direction="row" spacing={1} sx={{ alignItems: 'center', mb: 2 }}>
        <CircularProgress size={18} aria-hidden />
        <Typography variant="body2" color="text.secondary">
          Refreshing canvassing session diagnostics…
        </Typography>
      </Stack>
    )}
    <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ alignItems: { md: 'center' }, justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="body2" color="text.secondary">
            Canvasser
          </Typography>
          <Typography
            sx={{ overflowWrap: 'anywhere' }}
            aria-label={canvasserDirectoryStatus === 'loading' ? 'Loading canvasser name' : undefined}
          >
            {resolveCanvasserLabel(session.canvasserId)}
          </Typography>
        </Box>
        <Box>
          <Typography variant="body2" color="text.secondary">
            Started
          </Typography>
          <LocalTimestamp timestamp={session.startedAt} />
        </Box>
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
          <Chip label={getCanvassingSessionCaptureStatusLabel(session.captureStatus)} size="small" variant="outlined" />
          <Chip label={getCanvassingSessionProcessingStatusLabel(session.processingStatus)} size="small" variant="outlined" />
        </Stack>
      </Stack>
      {isStructurelyAdmin && (
        <>
          <Stack direction="row" spacing={3} useFlexGap sx={{ mt: 2, flexWrap: 'wrap' }}>
            <Box>
              <Typography variant="body2" color="text.secondary">
                App version
              </Typography>
              <Typography>{formatReportedAppVersion(session.appVersion)}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">
                OS
              </Typography>
              <Typography>{formatReportedOsVersion(session.platform, session.osVersion)}</Typography>
            </Box>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {SESSION_TELEMETRY_METADATA_NOTE}
          </Typography>
        </>
      )}
      <Stack spacing={0.5} sx={{ mt: 2 }}>
        <Typography variant="caption" color="text.secondary">
          Submitted means pass 1 resolved, not that all pass-2 or enrichment work is complete.
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Confirmed audio reflects persisted upload confirmation, not proof that the S3 object is currently available.
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Reported audio and GPS totals originate on the device and are completeness hints.
        </Typography>
      </Stack>
      {session.processingErrorAvailable && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          Processing error details are available in server logs.
        </Alert>
      )}
    </Paper>
  </>
);

const MetricsSection = ({ session }: { session: CanvassingSessionDetail }) => {
  const { metrics } = session;

  return (
    <SectionCard title="Capture and Processing Metrics">
      <Grid component="dl" container spacing={2} sx={{ m: 0 }}>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <Metric label="Capture duration" value={formatCanvassingSessionDuration(metrics.captureDurationSeconds)} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <Metric label="Audio received / expected" value={formatReceivedAndExpected(metrics.receivedAudioSegments, metrics.expectedAudioSegments)} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <Metric label="Confirmed audio duration" value={formatCanvassingSessionMilliseconds(metrics.confirmedAudioDurationMs)} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <Metric label="GPS received / reported" value={formatReceivedAndExpected(metrics.receivedGpsPoints, metrics.reportedGpsPoints)} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <Metric label="Total visits" value={metrics.doorVisitCount} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <Metric label="Clip-bearing visits" value={metrics.clipBearingDoorVisitCount} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <Metric label="Partial visits" value={metrics.partialDoorVisitCount} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <Metric label="Voter-matched visits" value={metrics.voterMatchedDoorVisitCount} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <Metric label="Core accepted visits" value={metrics.coreAcceptedDoorVisitCount} />
        </Grid>
      </Grid>
      <Grid container spacing={3} sx={{ mt: 1 }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
            Persisted visit-status counts
          </Typography>
          {metrics.doorVisitStatusCounts.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              —
            </Typography>
          ) : (
            <Stack spacing={0.5}>
              {metrics.doorVisitStatusCounts.map(item => (
                <Stack key={item.status} direction="row" spacing={1} sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
                  <Chip label={getCanvassingSessionDoorVisitStatusLabel(item.status)} size="small" variant="outlined" />
                  <Typography variant="body2">{item.count}</Typography>
                </Stack>
              ))}
            </Stack>
          )}
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
            Pipeline counts by stage and status
          </Typography>
          {metrics.pipelineStatusCounts.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              —
            </Typography>
          ) : (
            <Stack spacing={0.5}>
              {metrics.pipelineStatusCounts.map(item => (
                <Stack key={`${item.stage}-${item.status}`} direction="row" spacing={1} sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
                  <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                    <Chip label={getCanvassingSessionPipelineStageLabel(item.stage)} size="small" variant="outlined" />
                    <PipelineJobStatusChip status={item.status} />
                  </Stack>
                  <Typography variant="body2">{item.count}</Typography>
                </Stack>
              ))}
            </Stack>
          )}
        </Grid>
      </Grid>
    </SectionCard>
  );
};

const getTracePublication = (state: CanvassingSessionGpsTraceState): CanvassingSessionGpsTracePublication | null => {
  switch (state.status) {
    case 'loading-sampled':
      return state.publication;
    case 'sampled':
    case 'loading-full':
    case 'full':
      return state.publication;
    default:
      return null;
  }
};

type GpsTraceSectionProps = {
  sessionId: string;
  state: CanvassingSessionGpsTraceState;
  loadFull: () => void;
  retry: () => void;
  mapError: boolean;
  mapRetryKey: number;
  onMapError: () => void;
  onMapRetry: () => void;
  overlay: UseCanvassingSessionMapOverlayResult;
  gpsSettled: boolean;
  boundsAvailable: boolean;
};

const GpsTraceSection = ({
  sessionId,
  state,
  loadFull,
  retry,
  mapError,
  mapRetryKey,
  onMapError,
  onMapRetry,
  overlay,
  gpsSettled,
  boundsAvailable,
}: GpsTraceSectionProps) => {
  const publication = getTracePublication(state);
  const isLoadingFull = state.status === 'loading-full';
  const fittedGpsRevision = useRef<number | null>(null);
  const initialFitDone = useRef(false);
  const [cameraFitRevision, setCameraFitRevision] = useState(0);
  const hasPlottedRecords = overlay.voters.length > 0 || overlay.visits.length > 0;
  const showMap = (publication?.points.length ?? 0) > 0 || hasPlottedRecords || (boundsAvailable && publication !== null);
  const noMappedLocations = gpsSettled
    && (publication === null || publication.points.length === 0)
    && overlay.visits.length === 0
    && overlay.voters.length === 0
    && !overlay.visitsLoading
    && !overlay.votersLoading;

  useEffect(() => {
    if (!overlay.initialSettled || initialFitDone.current) return;
    initialFitDone.current = true;
    fittedGpsRevision.current = publication?.fitRevision ?? null;
    setCameraFitRevision(1);
  }, [overlay.initialSettled, publication?.fitRevision]);

  useEffect(() => {
    if (!initialFitDone.current) return;
    const revision = publication?.fitRevision;
    if (revision === undefined || fittedGpsRevision.current === revision) return;
    fittedGpsRevision.current = revision;
    setCameraFitRevision(current => current + 1);
  }, [publication?.fitRevision]);

  return (
    <SectionCard title="GPS trace" helper="GPS points are shown in chronological order from the authenticated session trace.">
      <Box sx={{ mb: 2 }}>
        <CanvassingSessionMapOverlayStatus overlay={overlay} gpsSettled={gpsSettled} boundsAvailable={boundsAvailable} />
      </Box>
      {state.status === 'loading-sampled' && publication === null && (
        <Stack role="status" aria-live="polite" direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <CircularProgress size={18} aria-hidden />
          <Typography variant="body2" color="text.secondary">Loading GPS trace…</Typography>
        </Stack>
      )}
      {state.status === 'unavailable' && (
        <Typography variant="body2" color="text.secondary">
          GPS trace is unavailable for this session.
        </Typography>
      )}
      {state.status === 'error' && (
        <Alert
          severity="error"
          action={<Button color="inherit" size="small" onClick={retry}>Retry GPS trace</Button>}
        >
          {state.error.message}
        </Alert>
      )}
      {publication !== null && (
        <Stack spacing={2}>
          {(state.status === 'sampled' || state.status === 'full') && state.refreshing && (
            <Stack role="status" aria-live="polite" direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <CircularProgress size={18} aria-hidden />
              <Typography variant="body2" color="text.secondary">Refreshing GPS trace…</Typography>
            </Stack>
          )}
          {isLoadingFull && (
            <Stack spacing={0.75} role="status" aria-live="polite">
              <Typography variant="body2" color="text.secondary">
                {state.progress === null
                  ? 'Preparing the full GPS trace…'
                  : `Loading full GPS trace: ${state.progress.loaded} / ${state.progress.total} points.`}
              </Typography>
              <LinearProgress
                variant={state.progress === null ? 'indeterminate' : 'determinate'}
                value={state.progress === null || state.progress.total === 0 ? undefined : (state.progress.loaded / state.progress.total) * 100}
                aria-label="Full GPS trace loading progress"
                aria-valuetext={state.progress === null ? 'Preparing full GPS trace' : `${state.progress.loaded} of ${state.progress.total} points loaded`}
              />
            </Stack>
          )}
          {publication.sampled && publication.totalCount > 2_000 && (
            <Alert
              severity="warning"
              action={
                <Button color="inherit" size="small" onClick={loadFull} disabled={isLoadingFull || state.status === 'sampled' && state.refreshing}>
                  Load full trace
                </Button>
              }
            >
              This map shows a 2,000-point sample of {publication.totalCount} recorded GPS points. Loading the full trace may affect browser performance.
            </Alert>
          )}
          {state.status === 'full' && (
            <Typography variant="body2" color="text.secondary">
              Showing all {publication.totalCount} points in the full trace.
            </Typography>
          )}
          {publication.invalidPointCount > 0 && (
            <Alert severity="warning">
              {publication.invalidPointCount} malformed GPS {publication.invalidPointCount === 1 ? 'point was' : 'points were'} excluded from the map.
            </Alert>
          )}
          {(state.status === 'sampled' || state.status === 'full') && state.error !== null && (
            <Alert
              severity="error"
              action={<Button color="inherit" size="small" onClick={retry}>Retry GPS trace</Button>}
            >
              {state.error.message}
            </Alert>
          )}
          {publication.points.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              No GPS points were recorded for this session.
            </Typography>
          )}
          {mapError ? (
            <Alert
              severity="error"
              action={<Button color="inherit" size="small" onClick={onMapRetry}>Retry map</Button>}
            >
              The GPS map could not be displayed. GPS trace diagnostics remain available above.
            </Alert>
          ) : showMap ? (
            <CanvassingSessionGpsTraceMap
              key={mapRetryKey}
              sessionId={sessionId}
              points={publication.points}
              displayedCount={publication.points.length}
              totalCount={publication.totalCount}
              fitRevision={publication.fitRevision}
              onMapError={onMapError}
              overlayVoters={overlay.voters}
              overlayVisits={overlay.visits}
              renderVoters={overlay.renderVoters}
              renderVisits={overlay.renderVisits}
              serverBounds={publication.bounds}
              cameraFitRevision={cameraFitRevision}
              showMapRecordsLegend={overlay.showVotersToggle}
            />
          ) : null}
        </Stack>
      )}
      {publication === null && hasPlottedRecords && !mapError && (
        <CanvassingSessionGpsTraceMap
          key={mapRetryKey}
          sessionId={sessionId}
          points={[]}
          displayedCount={0}
          totalCount={0}
          fitRevision={0}
          onMapError={onMapError}
          overlayVoters={overlay.voters}
          overlayVisits={overlay.visits}
          renderVoters={overlay.renderVoters}
          renderVisits={overlay.renderVisits}
          serverBounds={null}
          cameraFitRevision={cameraFitRevision}
          showMapRecordsLegend={overlay.showVotersToggle}
        />
      )}
      {noMappedLocations && (
        <Typography variant="body2" color="text.secondary">{MAP_OVERLAY_COPY.noLocations}</Typography>
      )}
      {overlay.showVotersToggle && (
        <Box sx={{ mt: 2 }}>
          <MapOverlayRecordList records={overlayRecordsInDisplayOrder(overlay.voters, overlay.visits)} />
        </Box>
      )}
    </SectionCard>
  );
};

const ActivitySection = ({ session }: { session: CanvassingSessionDetail }) => {
  const activity = [...session.activity].sort((left, right) => {
    const leftTime = new Date(String(left.occurredAt)).valueOf();
    const rightTime = new Date(String(right.occurredAt)).valueOf();
    return rightTime - leftTime;
  });

  return (
    <SectionCard
      title="Derived Activity"
      helper="This activity is reconstructed from current persisted timestamps and is not an authoritative audit log."
    >
      {activity.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          —
        </Typography>
      ) : (
        <Stack spacing={1}>
          {activity.map((item, index) => (
            <Stack
              key={`${item.kind}-${String(item.occurredAt)}-${index}`}
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              sx={{ justifyContent: 'space-between', borderBottom: '1px solid', borderColor: 'divider', pb: 1 }}
            >
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                <Typography variant="body2">{getCanvassingSessionActivityKindLabel(item.kind)}</Typography>
                {item.stage != null && <Chip label={getCanvassingSessionPipelineStageLabel(item.stage)} size="small" variant="outlined" />}
                {item.status != null && <PipelineJobStatusChip status={item.status} />}
              </Stack>
              <Typography variant="body2" color="text.secondary">
                <LocalTimestamp timestamp={item.occurredAt} />
              </Typography>
            </Stack>
          ))}
        </Stack>
      )}
    </SectionCard>
  );
};

type RetryDeadPipelineJobActionProps = {
  jobId: number;
  fetchImpl?: CanvassingSessionFetchImpl;
  onSuccess: () => void;
};

const RetryDeadPipelineJobAction = ({ jobId, fetchImpl, onSuccess }: RetryDeadPipelineJobActionProps) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [requestError, setRequestError] = useState<string | null>(null);
  const requestIdRef = useRef<string | null>(null);
  const submissionInFlightRef = useRef(false);

  const openDialog = (): void => {
    setRequestError(null);
    setDialogOpen(true);
  };

  const closeDialog = (): void => {
    if (submissionInFlightRef.current) {
      return;
    }

    setRequestError(null);
    setDialogOpen(false);
  };

  const confirmRetry = async (): Promise<void> => {
    if (submissionInFlightRef.current) {
      return;
    }

    const requestId = requestIdRef.current ?? crypto.randomUUID();
    requestIdRef.current = requestId;
    submissionInFlightRef.current = true;
    setRequestError(null);
    setIsSubmitting(true);

    try {
      const retriedJob = await retryDeadPipelineJob(jobId, requestId, {
        env: resolveClientEnv(),
        fetchImpl,
      });
      if (retriedJob === null) {
        setRequestError('Unable to retry this job.');
        return;
      }

      requestIdRef.current = null;
      setDialogOpen(false);
      onSuccess();
    } catch (error) {
      setRequestError(error instanceof Error ? error.message : 'Unable to retry this job.');
    } finally {
      submissionInFlightRef.current = false;
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        startIcon={<ReplayIcon />}
        aria-label={`Retry job ${jobId}`}
        onClick={openDialog}
      >
        Retry
      </Button>
      <Dialog
        open={dialogOpen}
        onClose={closeDialog}
        aria-labelledby={`retry-dead-pipeline-job-${jobId}-dialog-title`}
        aria-describedby={`retry-dead-pipeline-job-${jobId}-dialog-description`}
        aria-busy={isSubmitting}
        slotProps={{ paper: { 'aria-busy': isSubmitting } }}
      >
        <DialogTitle id={`retry-dead-pipeline-job-${jobId}-dialog-title`}>Retry dead job?</DialogTitle>
        <DialogContent>
          <DialogContentText id={`retry-dead-pipeline-job-${jobId}-dialog-description`}>
            This requeues job {jobId} with attempts reset to 0. A worker will pick it up. Door visit state is not changed.
          </DialogContentText>
          {isSubmitting && (
            <Stack direction="row" spacing={1} role="status" aria-live="polite" sx={{ alignItems: 'center', mt: 2 }}>
              <CircularProgress size={20} aria-label={`Retrying job ${jobId}`} />
              <Typography variant="body2">Retrying dead job…</Typography>
            </Stack>
          )}
          {requestError !== null && (
            <Alert severity="error" sx={{ mt: 2 }} aria-live="assertive">
              {requestError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button autoFocus onClick={closeDialog} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button variant="contained" onClick={() => void confirmRetry()} disabled={isSubmitting}>
            {isSubmitting ? 'Retrying…' : requestError === null ? 'Retry dead job' : 'Retry again'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

type PipelineProcessingSectionProps = {
  items: ReturnType<typeof useCanvassingSessionDetail>['pipelineJobs']['items'];
  total: number;
  page: number;
  rowsPerPage: number;
  refreshing: boolean;
  showJobActions: boolean;
  deletingJobId: number | null;
  deleteError: string | null;
  onDeleteJob: (jobId: number) => void;
  fetchImpl?: CanvassingSessionFetchImpl;
  onRetrySuccess: () => void;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
};

const pipelineJobDeleteLabel = (jobId: number): string => `Delete job ${jobId}`;

const PipelineProcessingSection = ({
  items,
  total,
  page,
  rowsPerPage,
  refreshing,
  showJobActions,
  deletingJobId,
  deleteError,
  onDeleteJob,
  fetchImpl,
  onRetrySuccess,
  onPageChange,
  onRowsPerPageChange,
}: PipelineProcessingSectionProps) => (
  <SectionCard
    title="Pipeline Processing"
    helper="Attempts count leases. Duplicate submit jobs are expected, and a completed job can retain an earlier error. Historical pass 1/pass 2 cannot be inferred from submit-job history."
  >
    <Alert severity="info" icon={false} sx={{ mb: 2 }}>
      Queued jobs await a worker; leased jobs have an active lease; done jobs completed; dead jobs exhausted processing; Failed (legacy) marks legacy state; unknown is unmapped. A dead job can coexist with a session that is not failed.
    </Alert>
    {showJobActions && deleteError !== null && (
      <Alert severity="error" sx={{ mb: 2 }}>
        {deleteError}
      </Alert>
    )}
    {items.length === 0 ? (
      <Typography variant="body2" color="text.secondary">
        No pipeline jobs have been recorded for this session.
      </Typography>
    ) : (
      <>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: showJobActions ? 1560 : 1320 }} aria-label="Pipeline Processing jobs">
            <TableHead>
              <TableRow>
                <TableCell>Job ID</TableCell>
                <TableCell>Stage</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Attempts</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Last updated</TableCell>
                <TableCell>Lease expiration</TableCell>
                <TableCell>Associated Door Visit</TableCell>
                <TableCell>Error summary</TableCell>
                {showJobActions && <TableCell>Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map(item => {
                const canAct = showJobActions && item.status === CanvassingSessionPipelineJobStatus.Dead;
                const isDeletingThisJob = deletingJobId === item.id;
                const deleteLabel = pipelineJobDeleteLabel(item.id);

                return (
                  <TableRow key={item.id} hover>
                    <TableCell>{item.id}</TableCell>
                    <TableCell>
                      <Chip label={getCanvassingSessionPipelineStageLabel(item.stage)} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <PipelineJobStatusChip status={item.status} />
                    </TableCell>
                    <TableCell>{item.attempts}</TableCell>
                    <TableCell><LocalTimestamp timestamp={item.createdAt} /></TableCell>
                    <TableCell><LocalTimestamp timestamp={item.updatedAt} /></TableCell>
                    <TableCell><LocalTimestamp timestamp={item.leaseExpiresAt} /></TableCell>
                    <TableCell><PipelineDoorVisitLink stage={item.stage} doorVisitId={item.doorVisitId} /></TableCell>
                    <TableCell sx={{ maxWidth: 360, overflowWrap: 'anywhere' }}>{sanitizeErrorSummary(item.errorSummary)}</TableCell>
                    {showJobActions && (
                      <TableCell>
                        {canAct && (
                          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                            <RetryDeadPipelineJobAction jobId={item.id} fetchImpl={fetchImpl} onSuccess={onRetrySuccess} />
                            <Tooltip title={deleteLabel}>
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={() => onDeleteJob(item.id)}
                                  disabled={deletingJobId !== null}
                                  aria-busy={isDeletingThisJob}
                                  aria-label={deleteLabel}
                                >
                                  <DeleteOutlinedIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Stack>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <CanvassingSessionDetailPagination
          label="Pipeline jobs"
          total={total}
          page={page}
          rowsPerPage={rowsPerPage}
          disabled={refreshing}
          onPageChange={onPageChange}
          onRowsPerPageChange={onRowsPerPageChange}
        />
      </>
    )}
  </SectionCard>
);

type SessionDoorVisitsSectionProps = {
  items: ReturnType<typeof useCanvassingSessionDetail>['doorVisits']['items'];
  total: number;
  page: number;
  rowsPerPage: number;
  refreshing: boolean;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
};

const SessionDoorVisitsSection = ({
  items,
  total,
  page,
  rowsPerPage,
  refreshing,
  onPageChange,
  onRowsPerPageChange,
}: SessionDoorVisitsSectionProps) => (
  <SectionCard title="Session Door Visits" helper="All server-scoped rows are shown, including no-speech records and reprocessed clones.">
    {items.length === 0 ? (
      <Typography variant="body2" color="text.secondary">
        No door visits were produced for this session.
      </Typography>
    ) : (
      <>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 2200 }} aria-label="Session Door Visits">
            <TableHead>
              <TableRow>
                <TableCell>Door Visit ID</TableCell>
                <TableCell>Dwell start</TableCell>
                <TableCell>Dwell end</TableCell>
                <TableCell>Persisted status</TableCell>
                <TableCell>Predicted address</TableCell>
                <TableCell>Voter name</TableCell>
                <TableCell>Clip present</TableCell>
                <TableCell>Partial</TableCell>
                <TableCell>Voter matched</TableCell>
                <TableCell>Core accepted</TableCell>
                <TableCell>Core recording confirmed</TableCell>
                <TableCell>Reprocessed</TableCell>
                <TableCell>Source Door Visit</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map(item => (
                <TableRow key={item.id} hover>
                  <TableCell><DoorVisitLink doorVisitId={item.id} /></TableCell>
                  <TableCell><LocalTimestamp timestamp={item.dwellStartedAt} /></TableCell>
                  <TableCell><LocalTimestamp timestamp={item.dwellEndedAt} /></TableCell>
                  <TableCell>
                    <Chip label={getCanvassingSessionDoorVisitStatusLabel(item.status)} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>{item.predictedAddress ?? '—'}</TableCell>
                  <TableCell>{item.voterName ?? '—'}</TableCell>
                  <TableCell>{formatNullableBoolean(item.hasClip)}</TableCell>
                  <TableCell>{formatNullableBoolean(item.isPartial)}</TableCell>
                  <TableCell>{formatNullableBoolean(item.isVoterMatched)}</TableCell>
                  <TableCell>{formatNullableBoolean(item.isCoreAccepted)}</TableCell>
                  <TableCell>{formatNullableBoolean(item.isCoreRecordingConfirmed)}</TableCell>
                  <TableCell>{item.isReprocessed ? <Chip label="Reprocessed" size="small" variant="outlined" /> : '—'}</TableCell>
                  <TableCell>
                    {isPositiveSafeInteger(item.sourceDoorVisitId) ? (
                      <MuiLink component={Link} to={`/door-visits/${item.sourceDoorVisitId}`}>
                        Source {item.sourceDoorVisitId}
                      </MuiLink>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <CanvassingSessionDetailPagination
          label="Session door visits"
          total={total}
          page={page}
          rowsPerPage={rowsPerPage}
          disabled={refreshing}
          onPageChange={onPageChange}
          onRowsPerPageChange={onRowsPerPageChange}
        />
      </>
    )}
  </SectionCard>
);

const CanvassingSessionDetailContent = ({
  sessionId,
  returnTo,
  fetchImpl,
  scheduler,
}: CanvassingSessionDetailPageProps & { sessionId: string; returnTo: string }) => {
  const detail = useCanvassingSessionDetail({ sessionId, fetchImpl, scheduler });
  const { isStructurelyAdmin } = useFeatureFlagsContext();
  const transcriptionPermitted = isStructurelyAdmin && detail.data?.id === sessionId;
  const recordingDescriptionId = useId();
  const recording = useCanvassingSessionRecording({
    sessionId,
    permitted: isStructurelyAdmin,
    fetchImpl,
  });
  const transcription = useCanvassingSessionTranscription({
    sessionId,
    permitted: transcriptionPermitted,
    fetchImpl,
  });
  const trace = useCanvassingSessionGpsTrace({ sessionId, fetchImpl });
  const tracePublication = getTracePublication(trace.state);
  const gpsRefreshing = trace.state.status === 'loading-sampled'
    || trace.state.status === 'loading-full'
    || ((trace.state.status === 'sampled' || trace.state.status === 'full') && trace.state.refreshing);
  const gpsFailed = trace.state.status === 'error'
    || ((trace.state.status === 'sampled' || trace.state.status === 'full') && trace.state.error !== null && !trace.state.refreshing);
  const gpsSettled = trace.state.status === 'sampled'
    || trace.state.status === 'full'
    || trace.state.status === 'unavailable'
    || trace.state.status === 'error'
    || trace.state.status === 'loading-full';
  const overlay = useCanvassingSessionMapOverlay({
    sessionId,
    isStructurelyAdmin,
    serverBounds: tracePublication?.bounds ?? null,
    boundsRevision: tracePublication?.fitRevision ?? 0,
    gpsSettled,
    gpsRefreshing,
    gpsFailed,
    fetchImpl,
  });
  const canvasserDirectory = useBrokerageCanvasserDirectory();
  const handlePipelineJobDeleted = useCallback((): void => {
    detail.refetch({ clampEmptyPipelineJobsPage: true });
  }, [detail.refetch]);
  const { deletingJobId, deleteError, deleteJob } = useDeleteCanvassingSessionPipelineJob({
    fetchImpl,
    onDeleted: handlePipelineJobDeleted,
  });
  const [mapError, setMapError] = useState(false);
  const [mapRetryKey, setMapRetryKey] = useState(0);

  useEffect(() => {
    setMapError(false);
  }, [tracePublication?.fitRevision]);

  const refreshDiagnostics = (): void => {
    detail.refetch();
    trace.refresh();
    overlay.refresh();
    if (transcriptionPermitted) transcription.refresh();
  };

  if (detail.loading) {
    return (
      <Box>
        <CanvassingSessionPageHeader returnTo={returnTo} sessionId={sessionId} />
        <LoadingState />
      </Box>
    );
  }

  if (detail.error !== null) {
    return (
      <Box>
        <CanvassingSessionPageHeader returnTo={returnTo} sessionId={sessionId} />
        <ErrorState
          title={detail.error.kind === 'forbidden' ? 'Permission required' : undefined}
          message={detail.error.kind === 'forbidden' ? detail.error.message : 'Unable to load this canvassing session.'}
          onRetry={() => detail.refetch()}
        />
      </Box>
    );
  }

  if (detail.data === null) return <UnavailableState returnTo={returnTo} sessionId={sessionId} />;

  const showRecordingControls = isStructurelyAdmin && detail.data.hasMergedRecording != null;
  const recordingUnavailable = detail.data.hasMergedRecording === false;

  return (
    <Box>
      <CanvassingSessionPageHeader returnTo={returnTo} sessionId={detail.data.id} />
      <SessionControlsBar
        refreshing={detail.refreshing}
        onRefresh={refreshDiagnostics}
        recordingControls={
          showRecordingControls ? (
            <SessionRecordingControls
              unavailable={recordingUnavailable}
              descriptionId={recordingDescriptionId}
              recording={recording}
            />
          ) : undefined
        }
        transcriptionControl={transcriptionPermitted ? (
          <CanvassingSessionTranscriptionAction
            state={transcription.state}
            sessionActive={detail.data.captureStatus === 'ACTIVE'}
            hasMergedRecording={detail.data.hasMergedRecording}
            submit={transcription.submit}
          />
        ) : undefined}
      />
      {showRecordingControls && (
        <SessionRecordingRegion
          unavailable={recordingUnavailable}
          descriptionId={recordingDescriptionId}
          recording={recording}
        />
      )}
      <Box component="section" aria-label="Canvassing session diagnostics" aria-busy={detail.refreshing}>
        <SessionOverview
          session={detail.data}
          isStructurelyAdmin={isStructurelyAdmin}
          manualRefreshing={detail.manualRefreshing}
          canvasserDirectoryStatus={canvasserDirectory.status}
          resolveCanvasserLabel={canvasserDirectory.resolveLabel}
        />
        <BrokerageCanvasserDirectoryFailureNotice />
        {transcriptionPermitted && (
          <Box sx={{ mb: 3 }}>
            <CanvassingSessionTranscriptionSection state={transcription.state} refresh={transcription.refresh} />
          </Box>
        )}
        {detail.backgroundError !== null && (
          <Alert
            severity="warning"
            sx={{ mb: 3 }}
            action={
              <Button color="inherit" size="small" onClick={() => detail.refetch()} disabled={detail.refreshing}>
                Retry
              </Button>
            }
          >
            Couldn&apos;t refresh canvassing session diagnostics. Displayed data may be incomplete.
          </Alert>
        )}
        <Stack spacing={3}>
          <MetricsSection session={detail.data} />
          <GpsTraceSection
            sessionId={detail.data.id}
            state={trace.state}
            loadFull={trace.loadFull}
            retry={trace.retry}
            mapError={mapError}
            mapRetryKey={mapRetryKey}
            onMapError={() => setMapError(true)}
            onMapRetry={() => {
              setMapError(false);
              setMapRetryKey(current => current + 1);
            }}
            overlay={overlay}
            gpsSettled={gpsSettled}
            boundsAvailable={tracePublication?.bounds != null}
          />
          <SessionDoorVisitsSection
            items={detail.doorVisits.items}
            total={detail.doorVisits.total}
            page={detail.doorVisitsPage}
            rowsPerPage={detail.doorVisitsRowsPerPage}
            refreshing={detail.refreshing}
            onPageChange={detail.setDoorVisitsPage}
            onRowsPerPageChange={detail.setDoorVisitsRowsPerPage}
          />
          <PipelineProcessingSection
            items={detail.pipelineJobs.items}
            total={detail.pipelineJobs.total}
            page={detail.pipelineJobsPage}
            rowsPerPage={detail.pipelineJobsRowsPerPage}
            refreshing={detail.refreshing}
            showJobActions={isStructurelyAdmin}
            deletingJobId={deletingJobId}
            deleteError={deleteError}
            onDeleteJob={jobId => deleteJob({ sessionId, jobId })}
            fetchImpl={fetchImpl}
            onRetrySuccess={() => detail.refetch()}
            onPageChange={detail.setPipelineJobsPage}
            onRowsPerPageChange={detail.setPipelineJobsRowsPerPage}
          />
          <CanvassingSessionTelemetrySection
            sessionId={detail.data.id}
            pollingRecommended={detail.data.pollingRecommended}
            fetchImpl={fetchImpl}
          />
          {isStructurelyAdmin && <ActivitySection session={detail.data} />}
        </Stack>
      </Box>
    </Box>
  );
};

export const CanvassingSessionDetailPage = ({ fetchImpl, scheduler }: CanvassingSessionDetailPageProps) => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const location = useLocation();
  const returnTo = resolveCanvassingSessionReturnTo(location.state);

  if (!isCanvassingSessionId(sessionId)) return <UnavailableState returnTo={returnTo} />;

  return (
    <CanvassingSessionDetailContent
      key={sessionId}
      sessionId={sessionId}
      returnTo={returnTo}
      fetchImpl={fetchImpl}
      scheduler={scheduler}
    />
  );
};
